import { QuestionSelectionType } from '@enums/QuestionSelectionTypes';
import { QuestionnaireType } from '@enums/QuestionnaireTypes';
import { ScoreCalculationType } from '@enums/ScoreCalculationTypes';
import { ScoringType } from '@enums/ScoringTypes';
import { ApiResponse } from '@interfaces/index';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiUrlConfig } from './configs/apiUrlConfig';
import createApiClient from './configs/httpHandler';

export const assessmentQuestionnaireApiHandler = {
  getQuestionsByQuestionnaire: async (
    questionnaireId: number,
  ): Promise<ApiResponse<QuestionnaireInterface>> =>
    createApiClient(apiUrlConfig.EXPRESS_BACKEND_V2).get(
      `/get-questions-by-questionnaire/${questionnaireId}`,
    ),

  createUserAssessment: async (
    payload: Partial<CreateUserAssessment>,
  ): Promise<ApiResponse<UserAssessmentResponse>> =>
    createApiClient(apiUrlConfig.EXPRESS_BACKEND_V2).post('/create-user-assessment', payload),

  getAllCompanyQuestionnaires: async (
    companyId: number,
  ): Promise<ApiResponse<QuestionnaireResponse[]>> =>
    createApiClient(apiUrlConfig.EXPRESS_BACKEND_V2).get(
      `/get-company-questionnaires/${companyId}`,
    ),

  getAllCompanyOnboardingQuestionnaires: async (): Promise<ApiResponse<QuestionnaireResponse[]>> =>
    createApiClient(apiUrlConfig.EXPRESS_BACKEND_V2).get(
      `/get-company-onboarding-questionnaires-for-user`,
    ),

  getQuestionnaireCategories: async (
    questionnaireId: number,
  ): Promise<ApiResponse<QuestionnaireCategoryInterface[]>> =>
    createApiClient(apiUrlConfig.EXPRESS_BACKEND_V2).get(
      `/get-questionnaire-categories/${questionnaireId}`,
    ),

  createReflection: async (
    payload: Partial<{ assessmentId: number | null; reflection: string }>,
  ): Promise<ApiResponse<CreateReflectionResponseInterface>> =>
    createApiClient(apiUrlConfig.EXPRESS_BACKEND_V2).post('/create-reflection', payload),

  saveEWSSpeakUp: async (
    payload: Partial<EWSSpeakUpPayload>,
  ): Promise<ApiResponse<EWSSpeakUpResponseInterface>> =>
    createApiClient(apiUrlConfig.EXPRESS_BACKEND_V2).post('/instant-help-speak-up', payload),

  createThought: async (
    payload: Partial<CreateThoughtPayload>,
  ): Promise<ApiResponse<ThoughtResponse[]>> =>
    createApiClient(apiUrlConfig.EXPRESS_BACKEND_V2).post('/create-thought', payload),

  getReflections: async (): Promise<ApiResponse<ReflectionsResponse[]>> =>
    createApiClient(apiUrlConfig.EXPRESS_BACKEND_V2).get(`/get-user-reflections`),

  getUserLatestAssessments: async (): Promise<ApiResponse<UserLatestAssessmentResponse[]>> =>
    createApiClient(apiUrlConfig.EXPRESS_BACKEND_V2).get(`/get-user-latest-assessments`),

  getUserDashboardCustomAssessments: async (): Promise<
    ApiResponse<DashboardCustomAssessmentResponse[]>
  > =>
    createApiClient(apiUrlConfig.EXPRESS_BACKEND_V2).get(`/get-user-dashboard-custom-assessments`),

  getUserAssessments: async (
    payload: Partial<{ questionnaireId: number; startDate: string | null; endDate: string | null }>,
  ): Promise<ApiResponse<UserAssessmentWithDetailsResponse[]>> =>
    createApiClient(apiUrlConfig.EXPRESS_BACKEND_V2).post(`/get-user-assessments`, payload),

  createTalkToAdvisorRequest: async (
    payload: Partial<{ assessmentId: number; focusAreaIds: number[] }>,
  ): Promise<ApiResponse<CreateTalkToAdvisorResponseInterface[]>> =>
    createApiClient(apiUrlConfig.EXPRESS_BACKEND_V2).post('/create-talk-to-advisor', payload),

  getUserAssessmentsByQuestionnaireType: async (
    questionnaireType: QuestionnaireType,
  ): Promise<ApiResponse<UserAssessment[]>> =>
    createApiClient(apiUrlConfig.EXPRESS_BACKEND_V2).get(
      `/get-user-assessments-by-questionnaire-type/${questionnaireType}`,
    ),

  getQuestionnaireDetailsByQuestionnaireId: async (
    questionnaireId: number,
  ): Promise<ApiResponse<QuestionnaireResponse>> =>
    createApiClient(apiUrlConfig.EXPRESS_BACKEND_V2).get(
      `/get-questionnaire-details/${questionnaireId}`,
    ),

  getQuestionnaireKeyInsight: async (
    questionnaireId: number,
  ): Promise<ApiResponse<QuestionnaireInsight>> =>
    createApiClient(apiUrlConfig.EXPRESS_BACKEND_V2).get(
      `/get-questionnaire-key-insights/${questionnaireId}`,
    ),

  getQuestionnaireQuestionGroups: async (
    questionnaireId: number,
  ): Promise<ApiResponse<QuestionnaireQuestionGroup[]>> =>
    createApiClient(apiUrlConfig.EXPRESS_BACKEND_V2).get(
      `/get-questionnaire-question-groups/${questionnaireId}`,
    ),

  getUserAssessmentQuestionGroupScores: async (
    userAssessmentId: number,
  ): Promise<ApiResponse<UserAssessmentQuestionGroupScoresResponse>> =>
    createApiClient(apiUrlConfig.EXPRESS_BACKEND_V2).get(
      `/get-user-assessment-question-group-scores/${userAssessmentId}`,
    ),

  createQuestionnaireInsight: async (
    payload: Partial<{
      questionnaireId: number;
      assessmentId: number;
      assessmentDate: string;
      /** Assessment-level free text for dimension insights (ids 18/19). Optional. */
      generalComment?: string | null;
    }>,
  ): Promise<ApiResponse<QuestionnaireInsight>> =>
    createApiClient(apiUrlConfig.EXPRESS_BACKEND_V2).post(`/create-questionnaire-insight`, payload),
};

export const useUserAssessments = (
  payload: Partial<{ questionnaireId: number; startDate: string | null; endDate: string | null }>,
) =>
  useQuery({
    queryKey: ['user-assessments', payload],
    queryFn: () => assessmentQuestionnaireApiHandler.getUserAssessments(payload),
    enabled: !!payload.questionnaireId && payload.questionnaireId !== 0, // optional: avoid running when payload is empty
  });

export const useUserAssessmentHooks = {
  // GET /get-user-assessments-by-questionnaire-type/{questionnaireType}
  useUserAssessmentsByQuestionnaireType: (questionnaireType: QuestionnaireType, enabled: boolean) =>
    useQuery({
      queryKey: ['user-assessments-by-questionnaire-type', questionnaireType],
      queryFn: () =>
        assessmentQuestionnaireApiHandler.getUserAssessmentsByQuestionnaireType(questionnaireType),
      enabled: enabled && !!questionnaireType,
      // staleTime: 5 * 60 * 1000, // 5 minutes
    }),

  useUserDashboardCustomAssessments: (enabled = true) =>
    useQuery({
      queryKey: ['user-dashboard-custom-assessments'],
      queryFn: () => assessmentQuestionnaireApiHandler.getUserDashboardCustomAssessments(),
      enabled,
    }),

  useCreateQuestionnaireInsight: () => {
    const queryClient = useQueryClient();

    return useMutation({
      mutationFn: (payload: {
        questionnaireId: number;
        assessmentId: number;
        assessmentDate: string;
        generalComment?: string | null;
      }) => assessmentQuestionnaireApiHandler.createQuestionnaireInsight(payload),
      onSuccess: (data, variables) => {
        // Invalidate related queries after successful creation
        queryClient.invalidateQueries({
          queryKey: ['questionnaire-key-insight', variables.questionnaireId],
        });
      },
      onError: (error) => {
        console.error('Failed to create questionnaire insight:', error);
      },
    });
  },

  useGetQuestionnaireKeyInsight: (questionnaireId: number | null) =>
    useQuery({
      queryKey: ['questionnaire-key-insight', questionnaireId],
      queryFn: () =>
        assessmentQuestionnaireApiHandler.getQuestionnaireKeyInsight(questionnaireId || 0),
      enabled: !!questionnaireId,
    }),

  useGetQuestionnaireQuestionGroups: (questionnaireId: number | null) =>
    useQuery({
      queryKey: ['questionnaire-question-groups', questionnaireId],
      queryFn: () =>
        assessmentQuestionnaireApiHandler.getQuestionnaireQuestionGroups(questionnaireId || 0),
      enabled: !!questionnaireId,
    }),

  useGetUserAssessmentQuestionGroupScores: (userAssessmentId: number | null) =>
    useQuery({
      queryKey: ['user-assessment-question-group-scores', userAssessmentId],
      queryFn: () =>
        assessmentQuestionnaireApiHandler.getUserAssessmentQuestionGroupScores(
          userAssessmentId || 0,
        ),
      enabled: !!userAssessmentId,
    }),
};

export const useQuestionnaireDetails = {
  // GET /get-user-assessments-by-questionnaire-type/{questionnaireType}
  useGetQuestionnaireDetails: (questionnaireId: number) =>
    useQuery({
      queryKey: ['questionnaire-by-questionnaire-id', questionnaireId],
      queryFn: () =>
        assessmentQuestionnaireApiHandler.getQuestionnaireDetailsByQuestionnaireId(questionnaireId),
      enabled: !!questionnaireId && questionnaireId !== 0,
      // staleTime: 5 * 60 * 1000, // 5 minutes
    }),
};

// Interface for the questionnaire response
export interface QuestionnaireInterface {
  id: number;
  name: string;
  shortDescription: string;
  description: string;
  version: string;
  type: string;
  default: boolean;
  createdAt: string;
  updatedAt: string;
  questionnairesV2Questions: QuestionInterface[];
  isThoughtsEnable: boolean;
  color: string | null;
  icon: string | null;
}

export interface QuestionCategoryRef {
  id: number;
  questionnaireId: number;
  name: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface QuestionCategoryJunction {
  questionnairesV2Categories?: QuestionCategoryRef | null;
}

export interface QuestionInterface {
  id: number;
  questionnaireId: number;
  categoryId: number;
  responseScaleId: number | null;
  selectionType?: QuestionSelectionType | string;
  commentEnabled?: boolean;
  commentRequired?: boolean;
  commentDescription?: string | null;
  questionText: string;
  orderId: number;
  required: boolean;
  icon: string | null;
  buttonText: string | null;
  description: string | null;
  isBrakeOff: boolean;
  createdAt: string;
  updatedAt: string;
  questionnairesV2QuestionResponseScales: QuestionResponseScale | null;
  questionnairesV2QuestionsCategories?: QuestionCategoryJunction[];
  label: string;
}

export interface QuestionResponseScale {
  id: number;
  scale: string;
  description: string;
  createdAt: string;
  updatedAt: string;
  questionnairesV2QuestionResponseScaleOptions: QuestionScaleOption[];
}

export interface QuestionScaleOption {
  id: number;
  scaleId: number;
  optionText: string;
  score: number;
  orderId: number;
  createdAt: string;
  updatedAt: string;
}

// Interface for creating user assessment
export interface CreateUserAssessment {
  questionnaireId: number;
  userResponses: UserResponse[];
}

export interface UserResponse {
  questionId: number;
  selectedOptionId?: number;
  selectedOptionIds?: number[];
  score?: number;
  commentText?: string | null;
}

// Response after submitting assessment
export interface UserAssessmentResponse {
  userId: number;
  questionnaireId: number;
  totalScore: number;
  id: number;
  createdAt: string;
  updatedAt: string;
  ewsTriggered: boolean;
}

// Interface for the company questionnaire
export interface CompanyQuestionnaireDataInterface {
  id: number;
  questionnaireId: number;
  companyId: number;
  orderId: number;
  active: boolean;
  createdAt: string;
  updatedAt: string;
  onboardOrderId?: number;
  isOnboard?: boolean;
  isThoughtsEnable: boolean;
  ewsLimit: number;
  ewsEnabled: number;
  questionnaireV2: QuestionnaireV2;
}

// Response questionnaire categories
export interface QuestionnaireCategoryInterface {
  id: number;
  questionnaireId: number;
  name: string;
  ewsEnabled: boolean;
  ewsLimit: number;
  createdAt: string;
  updatedAt: string;
}

// Response for create talk to advisor
export interface CreateTalkToAdvisorResponseInterface {
  id: number;
  userId: number;
  assessmentId: number;
  categoryId: number;
  createdAt: string;
  updatedAt: string;
}

// Response for create talk to advisor
export interface CreateReflectionResponseInterface {
  id: number;
  usersId: number;
  userAssessmentId: number;
  assessmentId: number | null;
  reflection: string;
  createdAt: string;
  updatedAt: string;
}

// EWS Speak Up Payload
export interface EWSSpeakUpPayload {
  assessmentId: number | null;
  message: string;
  shareMyIdentity: boolean;
  questionnaireId: number | null;
}

// EWS Speak Up Response
export interface EWSSpeakUpResponseInterface {
  userId: number;
  userAssessmentId: number;
  questionnaireV2Id: number;
  message: string;
  shareMyIdentity: boolean;
  responded: boolean;
  archived: boolean;
  id: number;
  createdAt: string; // ISO date string
  updatedAt: string; // ISO date string
}

// Create thought payload
export interface CreateThoughtPayload {
  assessmentId: number;
  thoughts: ThoughtInterface[];
}

export enum ThoughtShareWith {
  COMPANY = 'company',
  MYSELF = 'mySelf',
}

export interface ThoughtInterface {
  thought: string;
  shareWith: ThoughtShareWith.COMPANY | ThoughtShareWith.MYSELF;
  questionId: number;
}

// Create thought response
export interface ThoughtResponse {
  questionV2Id: number;
  userAssessmentId: number;
  shareWith: string;
  thoughtsText: string;
  responded: boolean;
  responsesSeen: boolean;
  id: number;
  questionId: number | null;
  assessmentId: number | null;
  createdAt: string;
  updatedAt: string;
}

// reflection response
export interface ReflectionsResponse {
  id: number;
  usersId: number;
  assessmentId: number | null;
  reflection: string;
  userAssessmentId: boolean | null;
  createdAt: string;
  updatedAt: string;
}

// User latest assessment response
export interface UserLatestAssessmentResponse {
  id: number;
  name: string;
  shortDescription: string;
  description: string;
  version: string;
  type: string;
  default: boolean;
  createdAt: string;
  updatedAt: string;
  scoringType: string;
  scoreCalculationType: string;
  outputType: string;
  factorSetId: number;
  questionnaireType: string;
  color: string | null;
  icon: string | null;
  maxScore: number;
  minScore: number;
  latestAssessment: {
    id: number;
    userId: number;
    questionnaireId: number;
    questionnaireV2: QuestionnaireV2;
    totalScore: number;
    createdAt: string;
    updatedAt: string;
  } | null;
}

/** GET /get-user-dashboard-custom-assessments */
export interface DashboardCustomAssessmentResponse {
  id: number;
  name: string;
  shortDescription?: string | null;
  description?: string | null;
  icon?: string | null;
  color?: string | null;
  questionnaireType: string;
  scoringType?: string;
  orderId: number;
  latestAssessment: {
    id: number;
    userId: number;
    questionnaireId: number;
    totalScore: number;
    createdAt: string;
    updatedAt: string;
  } | null;
}

/** GET /get-questionnaire-question-groups/:questionnaireId */
export interface QuestionnaireQuestionGroup {
  id: number;
  name: string;
  description: string | null;
  code: string | null;
  groupType: string | null;
  orderId: number;
}

/** GET /get-user-assessment-question-group-scores/:userAssessmentId */
export interface QuestionGroupScoreItem {
  id: number;
  name: string;
  description: string | null;
  code: string | null;
  groupType: string | null;
  orderId: number;
  totalScore: number | null;
}

export interface UserAssessmentQuestionGroupScoresResponse {
  userAssessmentId: number;
  questionnaireId: number;
  groups: QuestionGroupScoreItem[];
}

export interface UserAssessmentWithDetailsResponse {
  id: number;
  userId: number;
  questionnaireId: number;
  totalScore: number;
  createdAt: string;
  updatedAt: string;
  questionnaireV2: QuestionnaireV2;
  questionnairesV2UserResponses: {
    id: number;
    userAssessmentId: number;
    questionId: number;
    selectedOptionId: number;
    score: number;
    createdAt: string;
    updatedAt: string;
    questionnairesV2Questions: {
      id: number;
      questionnaireId: number;
      categoryId: number;
      responseScaleId: number;
      questionText: string;
      orderId: number;
      required: boolean;
      icon: string;
      label: string;
      buttonText: string;
      description: string;
      isBrakeOff: boolean;
      createdAt: string;
      updatedAt: string;
    };
  }[];
  questionnaireV2CategoryScores: {
    id: number;
    userAssessmentId: number;
    categoryId: number;
    totalScore: number;
    createdAt: string;
    updatedAt: string;
    questionnairesV2Categories: {
      id: number;
      questionnaireId: number;
      name: string;
      createdAt: string;
      updatedAt: string;
    };
  };
}

export interface QuestionnaireV2 {
  id: number;
  name: string;
  shortDescription: string;
  description: string;
  version: string;
  type: string;
  default: boolean;
  createdAt: string;
  updatedAt: string;
  scoringType:
    | ScoringType.CHECK_IN
    | ScoringType.LIKERT_MULTIPLE_QUESTIONS
    | ScoringType.LIKERT_MULTIPLE_QUESTIONS_WITH_COMMENTS
    | ScoringType.LIKERT_ONE_QUESTION;
  scoreCalculationType: ScoreCalculationType.HPS | ScoreCalculationType.SUM;
  outputType: string;
  factorSetId: number;
  questionnaireType: string;
  /** Present when questionnaireType is trauma — incident (TSQ) or compounding (STSS). */
  traumaPathway?: 'incident' | 'compounding' | null;
  color: string | null;
  icon: string | null;
  ewsTriggeredBy: string;
  maxScore: number;
  minScore: number;
  reverseScore: boolean;
}

export interface UserAssessment {
  id: number;
  userId: number;
  questionnaireId: number;
  totalScore: number;
  createdAt: string;
  updatedAt: string;
  questionnaireV2: QuestionnaireV2;
}

export interface QuestionnaireResponse {
  id: number;
  questionnaireId: number;
  companyId: number;
  orderId: number;
  active: boolean;
  createdAt: string;
  updatedAt: string;
  onboardOrderId?: number;
  isOnboard?: boolean;
  isThoughtsEnable: boolean;
  ewsLimit: number;
  ewsEnabled: number;
  questionnaireV2: QuestionnaireV2;
}

export interface QuestionnaireInsight {
  id: number;
  userId: number;
  questionnaireId: number;
  insight: string[];
  trigger: {
    assessmentId: number;
  };
  createdAt: string; // ISO date string
  updatedAt: string; // ISO date string
  recommendedActions: string[] | null;
}
